import React, { useEffect, useState, useCallback } from "react";
import { IconButton, Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useAppDispatch, useAppSelector } from "../../../../store/types";
import { updateCallHistory } from "../../../../services/history";
import { updateCurrentCtr } from "../../../../services/current";
import SbuxTextField from "../../../../components/SubxTextField";
import {
  CURRENT_TICKET_OR_CASE_NUMBER,
  CURRENT_VAR_TICKET_OR_CASE_NUMBER,
} from "../../../../constants";
import styles from "../styles";
import useCss from "../../../../hooks/useCss";
import { getInstanceIdSelector } from "../../../../selectors/userInstanceSelector";

interface TableRowData {
  name: string;
  value: string;
  isEditMode?: boolean;
}

interface InputTableCellProps {
  row: TableRowData;
  contactId: string | null;
  timeStamp: string;
}

const InputTableCell = ({ row, contactId, timeStamp }: InputTableCellProps) => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();
  const [tableRow, setTableRow] = useState<TableRowData>(row);
  const [previous, setPrevious] = useState<TableRowData>(row);

  const instanceId = useAppSelector(getInstanceIdSelector);

  useEffect(() => {
    setPrevious(row);
  }, [row]);

  const { isEditMode } = tableRow;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setTableRow((prevState) => ({
      ...prevState,
      value,
    }));
  }, []);

  const handleCancelMode = useCallback(() => {
    setTableRow({ ...previous, isEditMode: false });
  }, [previous]);

  const handleEditMode = useCallback(() => {
    setTableRow((prevState) => ({
      ...prevState,
      isEditMode: true,
    }));
  }, []);

  const handleSaveMode = useCallback(async () => {
    const ticketOrCaseNumberValue = tableRow.value;
    const updateHistoryData = {
      timestamp: timeStamp,
      updates: {
        [CURRENT_TICKET_OR_CASE_NUMBER]: ticketOrCaseNumberValue,
        [CURRENT_VAR_TICKET_OR_CASE_NUMBER]: ticketOrCaseNumberValue,
      },
    };
    setTableRow((prevState) => ({
      ...prevState,
      isEditMode: false,
    }));
    await dispatch(updateCallHistory(updateHistoryData));
    await dispatch(
      updateCurrentCtr({ instanceId, contactId, ticketOrCaseNumberValue }),
    );
  }, [tableRow]);

  return (
    <>
      {isEditMode ? (
        <Box sx={{ display: "flex", alignItems: "flex-start", flexWrap: "nowrap", width: "100%", gap: 0.5 }}>
          <SbuxTextField
            value={tableRow.value}
            handleChange={handleChange}
            multiline={tableRow.value.length > 20}
            maxLength={100}
            displayCounter
          />
          <IconButton
            disabled={tableRow.value.trim() === ""}
            aria-label="save"
            color="primary"
            size="small"
            onClick={handleSaveMode}
            sx={{ mt: 0.5 }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
          <IconButton aria-label="cancel" size="small" onClick={handleCancelMode} sx={{ mt: 0.5 }}>
            <CancelIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "nowrap", width: "100%" }}>
          <Box className={classes.inputValue}>{tableRow.value}</Box>
          <IconButton
            aria-label="edit"
            color="primary"
            size="small"
            onClick={handleEditMode}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      )}
    </>
  );
};

export default InputTableCell;
